import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Send, X, Loader2, Mail, Phone as PhoneIcon, Hash, Check } from "lucide-react";
import { API } from "../config";
import PhoneInput from "./shared/PhoneInput";

/**
 * Modal that lets a customer flag an error in the dealer-pre-filled
 * data (email / phone / tax_code) BEFORE proceeding with the wallet
 * signature procedure.
 *
 * Fabrizio CR 31/07/2026: when the customer submits the correction
 * request the wallet procedure is halted server-side. The dealer
 * receives an email, corrects the field in the portal, and a fresh
 * wallet-onboarding email is dispatched to the (possibly new) address.
 * The customer restarts the flow from Step 1.
 */
const FIELD_OPTIONS = [
  { value: "email", labelKey: "field_email", icon: Mail },
  { value: "phone", labelKey: "field_phone", icon: PhoneIcon },
  { value: "tax_code", labelKey: "field_tax_code", icon: Hash },
];

export default function CorrectionRequestModal({
  customer,
  onClose,
  onSubmitted,
  translations = {},
}) {
  const t = {
    title: translations.title || "Segnala un errore nei dati precompilati",
    subtitle:
      translations.subtitle ||
      "Se il concessionario ha inserito un dato errato, richiedi la correzione qui. La procedura si interromperà finché il concessionario non aggiornerà i dati e non ti reinvierà una nuova email di attivazione.",
    field_label: translations.field_label || "Quale campo è errato?",
    current_label: translations.current_label || "Valore attualmente inserito",
    new_label: translations.new_label || "Valore corretto",
    note_label: translations.note_label || "Note *",
    note_placeholder:
      translations.note_placeholder ||
      "Es. il codice fiscale corretto è XXX perché mi chiamo XXX...",
    submit: translations.submit || "Invia richiesta al concessionario",
    cancel: translations.cancel || "Annulla",
    warning_title: translations.warning_title || "Attenzione",
    warning_body:
      translations.warning_body ||
      "Se richiedi la modifica dell'email, il nuovo link wallet arriverà al nuovo indirizzo — assicurati che sia scritto correttamente. Il vecchio link resterà inutilizzabile.",
    success_toast:
      translations.success_toast ||
      "Richiesta inviata al concessionario. Riceverai una nuova email non appena i dati saranno corretti.",
    validation_field: translations.validation_field || "Seleziona il campo errato",
    validation_new_value:
      translations.validation_new_value || "Inserisci il valore corretto",
    validation_same:
      translations.validation_same ||
      "Il nuovo valore è identico a quello attuale.",
    // Fabrizio CR 07/02/2026 — the correction note is now MANDATORY:
    // dealers need context to know what to fix, and a mandatory note
    // reduces the number of empty "please check" requests they see.
    validation_note:
      translations.validation_note ||
      "La nota è obbligatoria: descrivi brevemente cosa va corretto.",
    // Zorzutti CR 01/08/2026 — double-entry confirmation to catch typos
    confirm_label:
      translations.confirm_label || "Ripeti il valore corretto per conferma",
    confirm_placeholder:
      translations.confirm_placeholder ||
      "Reinserisci esattamente lo stesso valore",
    validation_mismatch:
      translations.validation_mismatch ||
      "I due valori inseriti non coincidono. Verifica e riprova.",
    match_hint_ok: translations.match_hint_ok || "I due valori coincidono",
    // Zorzutti CR 03/08/2026 — field-picker labels were hardcoded in
    // Italian, leaking into every non-IT locale (screenshot from the
    // Polish flow showed "Cellulare 1 / Codice Fiscale" untranslated).
    field_email: translations.field_email || "Email",
    field_phone: translations.field_phone || "Cellulare",
    field_tax_code: translations.field_tax_code || "Codice Fiscale",
  };

  const [field, setField] = useState("");
  // For phone we keep prefix+number separated locally; for email/tax_code
  // we use a single string. When submitting we always send a single
  // joined ``new_value`` string in the payload (backend handles the
  // phone split, see dealer.py:dealer_approve_change_request).
  const [newValue, setNewValue] = useState("");
  const [newValueConfirm, setNewValueConfirm] = useState("");
  const [phonePrefix, setPhonePrefix] = useState(
    customer?.phone_prefix || "+39"
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phonePrefixConfirm, setPhonePrefixConfirm] = useState(
    customer?.phone_prefix || "+39"
  );
  const [phoneNumberConfirm, setPhoneNumberConfirm] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Compose the primary + confirmation "canonical" strings so the two
  // are compared with the same normalisation everywhere below.
  const composeValue = (f, primary) => {
    if (f === "phone") {
      const p = primary
        ? { prefix: phonePrefix, number: phoneNumber }
        : { prefix: phonePrefixConfirm, number: phoneNumberConfirm };
      const digits = (p.number || "").replace(/\D/g, "");
      return digits ? `${p.prefix} ${digits}` : "";
    }
    return (primary ? newValue : newValueConfirm).trim();
  };

  // Case-insensitive comparison for email + tax_code (naturally
  // case-insensitive); strict comparison for phone (already normalised).
  const normalizeForCompare = (f, s) => {
    if (!s) return "";
    if (f === "phone") return s.replace(/\s+/g, "");
    return s.trim().toLowerCase();
  };
  const primaryValue = composeValue(field, true);
  const confirmValue = composeValue(field, false);
  const valuesMatch =
    primaryValue &&
    confirmValue &&
    normalizeForCompare(field, primaryValue) ===
      normalizeForCompare(field, confirmValue);

  const currentValueOfSelectedField = (() => {
    if (!field || !customer) return "";
    if (field === "email") return customer.email || "";
    if (field === "phone")
      return `${customer.phone_prefix || ""} ${customer.phone || ""}`.trim();
    if (field === "tax_code") return customer.tax_code || "";
    return "";
  })();

  const handleSubmit = async () => {
    if (!field) {
      toast.error(t.validation_field);
      return;
    }
    if (!primaryValue) {
      toast.error(t.validation_new_value);
      return;
    }
    if (!valuesMatch) {
      toast.error(t.validation_mismatch);
      return;
    }
    if (
      normalizeForCompare(field, primaryValue) ===
      normalizeForCompare(field, currentValueOfSelectedField)
    ) {
      toast.error(t.validation_same);
      return;
    }
    // Fabrizio CR 07/02/2026 — mandatory correction note. A minimum
    // length of 5 chars catches accidental "ok"/"a" submissions but
    // is loose enough not to annoy legit users (e.g. "typo" / "sbagliato"
    // count as valid).
    const trimmedNote = note.trim();
    if (trimmedNote.length < 5) {
      toast.error(t.validation_note);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/customers/${customer.id}/wallet/correction-request`,
        {
          field,
          new_value: primaryValue,
          note: trimmedNote,
        }
      );
      toast.success(t.success_toast);
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Errore invio richiesta";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="correction-request-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-[#141414] border border-gray-800 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-900/20 border border-red-800/40">
              <AlertTriangle size={18} className="text-red-400" />
            </div>
            <h2 className="text-white font-semibold text-lg">{t.title}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-40"
            data-testid="correction-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <p className="text-gray-400 text-sm leading-relaxed">{t.subtitle}</p>

          <div>
            <label className="text-gray-300 text-sm font-medium block mb-2">
              {t.field_label}
            </label>
            <div className="grid grid-cols-1 gap-2">
              {FIELD_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const active = field === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setField(opt.value)}
                    disabled={submitting}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                      active
                        ? "border-lojack-red bg-red-900/20 text-white"
                        : "border-gray-800 bg-[#0A0A0A] text-gray-300 hover:border-gray-600"
                    }`}
                    data-testid={`correction-field-${opt.value}`}
                  >
                    <Icon
                      size={16}
                      className={active ? "text-lojack-red" : "text-gray-500"}
                    />
                    <span className="font-medium">{t[opt.labelKey]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {field && (
            <>
              <div>
                <label className="text-gray-400 text-xs block mb-1">
                  {t.current_label}
                </label>
                <input
                  value={currentValueOfSelectedField || "(vuoto)"}
                  disabled
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-500 cursor-not-allowed text-sm font-mono"
                  data-testid="correction-current-value"
                />
              </div>

              <div>
                <label className="text-gray-300 text-sm font-medium block mb-1">
                  {t.new_label} *
                </label>
                {field === "phone" ? (
                  <PhoneInput
                    prefix={phonePrefix}
                    number={phoneNumber}
                    onChange={({ prefix, number }) => {
                      setPhonePrefix(prefix);
                      setPhoneNumber(number);
                    }}
                    disabled={submitting}
                    testId="correction-new-value"
                    placeholder="3311234567"
                    customerCountry={customer?.country}
                  />
                ) : (
                  <input
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    disabled={submitting}
                    autoFocus
                    className="w-full px-3 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-lojack-red focus:outline-none font-mono"
                    data-testid="correction-new-value"
                    placeholder={
                      field === "email"
                        ? "mario.rossi@example.com"
                        : "RSSMRA80A01H501U"
                    }
                  />
                )}
              </div>

              {/* Double-entry confirmation (Zorzutti CR 01/08/2026) —
                  catches typos BEFORE the dealer is notified, cutting
                  wasted correction-cycle churn. Live check: if both
                  fields are filled and match → green banner with
                  Check icon; if filled but differ → red hint. */}
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-1">
                  {t.confirm_label} *
                </label>
                {field === "phone" ? (
                  <PhoneInput
                    prefix={phonePrefixConfirm}
                    number={phoneNumberConfirm}
                    onChange={({ prefix, number }) => {
                      setPhonePrefixConfirm(prefix);
                      setPhoneNumberConfirm(number);
                    }}
                    disabled={submitting}
                    testId="correction-new-value-confirm"
                    placeholder="3311234567"
                    customerCountry={customer?.country}
                  />
                ) : (
                  <input
                    value={newValueConfirm}
                    onChange={(e) => setNewValueConfirm(e.target.value)}
                    disabled={submitting}
                    className="w-full px-3 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-lojack-red focus:outline-none font-mono"
                    data-testid="correction-new-value-confirm"
                    placeholder={t.confirm_placeholder}
                    onPaste={(e) => {
                      // Prevent copy-paste on the CONFIRM field — the
                      // whole point of double-entry is that the user
                      // re-types it. Pasting defeats the purpose.
                      e.preventDefault();
                      toast.info(t.confirm_placeholder);
                    }}
                  />
                )}
                {/* Live match indicator */}
                {primaryValue && confirmValue && (
                  valuesMatch ? (
                    <p
                      className="mt-1.5 text-xs text-emerald-400 flex items-center gap-1.5"
                      data-testid="correction-confirm-match-ok"
                    >
                      <Check size={12} />
                      {t.match_hint_ok}
                    </p>
                  ) : (
                    <p
                      className="mt-1.5 text-xs text-red-400 flex items-center gap-1.5"
                      data-testid="correction-confirm-mismatch"
                    >
                      <AlertTriangle size={12} />
                      {t.validation_mismatch}
                    </p>
                  )
                )}
              </div>

              <div>
                <label className="text-gray-400 text-xs block mb-1">
                  {t.note_label}
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={submitting}
                  rows={3}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-lojack-red focus:outline-none text-sm resize-none"
                  data-testid="correction-note"
                  placeholder={t.note_placeholder}
                />
              </div>

              <div className="rounded-lg border border-amber-800/50 bg-amber-950/20 p-3">
                <div className="flex gap-2">
                  <AlertTriangle
                    size={14}
                    className="text-amber-400 mt-0.5 flex-shrink-0"
                  />
                  <div className="text-amber-200 text-xs leading-relaxed">
                    <strong className="text-amber-300">{t.warning_title}:</strong>{" "}
                    {t.warning_body}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-end gap-2 bg-[#0A0A0A]">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm font-medium disabled:opacity-40"
            data-testid="correction-cancel-btn"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !field || !primaryValue || !valuesMatch || note.trim().length < 5}
            className="px-5 py-2 rounded-lg bg-lojack-red text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="correction-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Invio…
              </>
            ) : (
              <>
                <Send size={14} />
                {t.submit}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
